import React, { useState, useEffect, useRef } from 'react';
import Fade from 'react-reveal/Fade';
import { useSite } from '../../context/SiteContext';
import Skeleton from 'react-loading-skeleton';

// IMAGES
import imgBulk from '../../assets/business/bulktransfer.png';
import imgInvoice from '../../assets/business/invoicing.png';
import imgPos from '../../assets/business/offlinepayment.png';
import imgLoans from '../../assets/business/businessloans.png';
import imgAccounts from '../../assets/business/multiaccount.png';
import imgRecords from '../../assets/business/realtimerecords.png';

// ICONS
const IconUsers = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7ZM23 21V19C23 17.1648 21.7628 15.618 20.091 15.1165M16 3.12611C17.7252 3.61481 19 5.15813 19 7C19 8.84187 17.7252 10.3852 16 10.8739" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconBanknote = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const IconCheckCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M22 11.08V12C21.9981 14.1564 21.3005 16.2547 20.0093 17.9895C18.7182 19.7244 16.9033 21.0026 14.8354 21.6315C12.7674 22.2604 10.5507 22.2069 8.51703 21.4789C6.48339 20.751 4.74058 19.387 3.54587 17.5922C2.35116 15.7974 1.76518 13.668 1.87462 11.5167C1.98407 9.36539 2.78301 7.3088 4.15302 5.6503C5.52303 3.9918 7.39162 2.8211 9.48192 2.31385C11.5722 1.80661 13.7749 1.98895 15.75 2.83M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function BusinessBankingSectionView({ onSignUp }) {
  const { bankingDetails, loading } = useSite();
  const business = bankingDetails?.business;
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const defaultBullets = [
    'Bulk Transfers — Pay staff, suppliers, and vendors instantly.',
    'Invoicing — Create and settle invoices directly from your dashboard.',
    'Business POS — Accept payments anywhere with WayaBank POS.',
    'Business Loans — Access capital when your business needs it.',
    'Multi-Account Banking — Manage business and personal finances separately.',
    'Realtime Records — Monitor your transactions and cash flow in real-time.',
  ];

  return (
    <section id="business" className="waya-business-section" ref={sectionRef}>
      <div className="section-glow-accent" />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ textAlign: 'center' }}>
          <Fade bottom>
            <span className="waya-eyebrow-business">
              {loading ? <Skeleton width={120} /> : (business?.sectionTagline || 'BUSINESS BANKING')}
            </span>
            <h2 className="waya-section-heading">
              {loading ? <Skeleton width="70%" /> : (business?.title || 'Built for the market, not just the office.')}
            </h2>
            <p className="waya-section-subline">
              {loading ? <Skeleton count={3} /> : (business?.description || 'WayaBank is also building for Mama Bisi. The tomato seller. The mama put owner. The market trader who has never been inside a bank branch and deserves one anyway. Whether you sell tomatoes or run a growing business, WayaBank works for you.')}
            </p>
          </Fade>

          <div className={`waya-business-grid ${isInView ? 'active' : ''}`}>
            {(loading ? Array.from({ length: 6 }) : (business?.bullets || defaultBullets).slice(0, 6)).map((bullet, idx) => {
              const [title, description] = bullet ? bullet.split(' — ') : ['', ''];
              const images = [imgBulk, imgInvoice, imgPos, imgLoans, imgAccounts, imgRecords];

              return (
                <Fade bottom delay={idx * 100} key={idx}>
                  <div className="waya-business-card">
                    <div className="card-image-bg">
                      <img src={images[idx % images.length]} alt="" />
                    </div>
                    <div className="card-content-inner">
                      {loading ? (
                        <Skeleton height={150} borderRadius={20} />
                      ) : (
                        <>
                          <div className="icon-container-business">
                            <IconCheckCircle />
                          </div>
                          <h3 className="card-title-business">{title}</h3>
                          <p className="card-description-business">{description}</p>
                        </>
                      )}
                    </div>
                  </div>
                </Fade>
              );
            })}

            {/* Duplicate for mobile marquee */}
            {!loading && (business?.bullets || defaultBullets).slice(0, 6).map((bullet, idx) => {
              const [title, description] = bullet ? bullet.split(' — ') : ['', ''];
              const images = [imgBulk, imgInvoice, imgPos, imgLoans, imgAccounts, imgRecords];

              return (
                <div key={`dup-${idx}`} className="waya-business-card d-md-none">
                  <div className="card-image-bg">
                    <img src={images[idx % images.length]} alt="" />
                  </div>
                  <div className="card-content-inner">
                    <div className="icon-container-business">
                      <IconCheckCircle />
                    </div>
                    <h3 className="card-title-business">{title}</h3>
                    <p className="card-description-business">{description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <Fade bottom delay={400}>
            <div className="waya-stats-container">
              {loading ? (
                <>
                  <Skeleton width={180} height={80} borderRadius={20} />
                  <Skeleton width={180} height={80} borderRadius={20} />
                </>
              ) : (
                business?.stats?.map((stat, idx) => (
                  <div key={idx} className="waya-stat-chip">
                    <div className="waya-stat-icon-bg">
                      {idx === 0 ? <IconUsers /> : <IconBanknote />}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div className="stat-value">{stat.value}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Fade>

          <Fade bottom delay={500}>
            <div style={{ marginTop: '64px' }}>
              <button className="btn-white-business" onClick={onSignUp}>
                {loading ? <Skeleton width={160} height={20} /> : (business?.buttonText || 'Open Business Account')}
              </button>
            </div>
          </Fade>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .waya-business-section {
          background: #ff4400;
          padding: 160px 24px;
          position: relative;
          overflow: hidden;
          font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif;
        }

        .section-glow-accent {
          position: absolute;
          top: 20%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 800px;
          height: 800px;
          background: radial-gradient(circle, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 70%);
          pointer-events: none;
          z-index: 1;
        }

        .waya-eyebrow-business {
          color: rgba(0,0,0,0.6);
          font-size: 14px;
          font-weight: 800;
          letter-spacing: 5px;
          display: block;
          margin-bottom: 24px;
          text-transform: uppercase;
        }

        .waya-section-heading {
          color: #000000;
          font-size: clamp(38px, 6vw, 64px);
          font-weight: 800;
          margin-bottom: 32px;
          letter-spacing: -0.04em;
          line-height: 1.15;
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;
          padding-top: 20px;
        }

        .waya-section-subline {
          color: #000000;
          font-size: 20px;
          max-width: 780px;
          margin: 0 auto 80px;
          line-height: 1.7;
          font-weight: 500;
          opacity: 0.85;
        }

        .waya-business-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 32px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .waya-business-card {
          position: relative;
          background: #fff;
          border-radius: 32px;
          overflow: hidden;
          height: 380px;
          transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          border: 1px solid rgba(255,255,255,0.2);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 0;
          cursor: pointer;
          width: calc(33.333% - 32px);
          min-width: 320px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .card-image-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .card-image-bg img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .waya-business-card:hover .card-image-bg img {
          transform: scale(1.12);
        }

        .card-content-inner {
          position: relative;
          z-index: 2;
          text-align: left;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          padding: 20px;
          margin: 12px;
          border-radius: 24px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.15);
          border: 1px solid rgba(255,255,255,0.4);
          transition: all 0.3s ease;
        }

        .waya-business-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 40px 80px -15px rgba(0,0,0,0.3);
        }

        .icon-container-business {
          width: 40px;
          height: 40px;
          background: #ff4400;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          margin-bottom: 12px;
          box-shadow: 0 8px 16px rgba(255,68,0,0.2);
          transition: transform 0.3s ease;
        }

        .waya-business-card:hover .icon-container-business {
          transform: scale(1.1) rotate(-5deg);
        }

        .card-title-business {
          font-size: 18px;
          font-weight: 800;
          color: #000;
          margin-bottom: 6px;
          line-height: 1.2;
          letter-spacing: -0.01em;
        }

        .card-description-business {
          font-size: 13px;
          color: rgba(0,0,0,0.7);
          line-height: 1.4;
          margin: 0;
          font-weight: 500;
        }

        .waya-stats-container {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 24px;
          margin-top: 80px;
        }

        .waya-stat-chip {
          background: #ffffff;
          border-radius: 28px;
          padding: 24px 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 18px;
          min-width: 240px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }

        .waya-stat-chip:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 45px rgba(0,0,0,0.15);
        }

        .stat-value {
          font-size: 28px;
          font-weight: 800;
          color: #0f172a;
          line-height: 1;
        }

        .stat-label {
          font-size: 14px;
          color: #64748b;
          margin-top: 6px;
          font-weight: 600;
        }

        .waya-stat-icon-bg {
          width: 52px;
          height: 52px;
          border-radius: 16px;
          background: #fff5f0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ff4400;
        }

        .btn-white-business {
          background: #ffffff;
          color: #ff4400;
          padding: 22px 64px;
          border-radius: 18px;
          font-weight: 700;
          font-size: 17px;
          border: none;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          cursor: pointer;
        }

        .btn-white-business:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 25px 50px rgba(0,0,0,0.25);
          background: #fdfdfd;
        }

        @media (max-width: 1200px) {
          .waya-business-card {
            width: calc(50% - 32px);
          }
        }

        @media (max-width: 768px) {
          .waya-business-section {
            padding: 100px 24px 140px;
          }
          .waya-business-grid {
            display: flex;
            flex-wrap: nowrap;
            overflow: visible;
            justify-content: flex-start;
            padding: 20px 0;
            gap: 20px;
            width: max-content;
          }
          .waya-business-grid.active {
            animation: wayaMarquee 35s linear infinite;
          }
          .waya-business-grid:hover {
            animation-play-state: paused;
          }
          .waya-business-grid > div {
            flex: 0 0 320px;
          }
          .waya-section-heading {
            font-size: 38px;
          }
          .waya-business-section {
            overflow: hidden;
          }
        }
      `}</style>
    </section>
  );
}
